/**
 * Shared tokens and auth-error translation for the light split-card login.
 *
 * Black and white on white, Inter — the same language as the landing page
 * (`marketing/hero/hero-tokens.ts`). Portal hosts are the exception: those
 * screens run on the organisation's own brand color, passed in at runtime.
 *
 * The auth-error copy lives in `<locale>/public.json` under `loginShared`;
 * `translateAuthError()` takes a translator scoped to that section. The map
 * keys stay untouched — they are Better-Auth codes matched against, not copy.
 */

import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";

/** Primary accent on our own auth screens: ink, not verdigris. Inverts in dark mode. */
export const ACCENT = ld("#0B0F19", "#fafafa");

/** Text on the accent-filled submit button. */
export const ACCENT_TEXT = ld("#ffffff", "#181b1f");

/** Shadow tint under the submit button. */
export const ACCENT_SHADOW = ld("rgba(11,15,25,0.35)", "rgba(0,0,0,0.6)");

/** Focus ring shown around inputs while focused. */
export const FOCUS_SHADOW = `0 0 0 3px ${ld("rgba(11,15,25,0.12)", "rgba(250,250,250,0.16)")}`;

/** Surface tokens. Light unless the staff login follows the website's dark mode. */
export const PAGE_BG = ld("#F4F4F5", "#121417");
export const CARD_BG = ld("#FFFFFF", "#181b1f");
export const INK = ld("#0B0F19", "#fafafa");
export const DARK_PANEL_BG = ld("#0B0F19", "#1D2024");
export const INPUT_BG = ld("#FFFFFF", "#1f2225");
export const INPUT_BORDER = ld("#E7E9ED", "#32363a");

/** Better-Auth code/message → message key. Keys are matched, never displayed. */
function authErrorKeyMap(): Record<string, string> {
  return {
    "Invalid email or password": "ungueltigeEMailPasswort",
    "Invalid email": "ungueltigeEMailAdresse",
    "Invalid password": "ungueltigesPasswort",
    "User not found": "benutzerNichtGefunden",
    "User already exists": "benutzerDieserEMail",
    "User already exists. Use another email.": "benutzerDieserEMail",
    "Email is not verified": "eMailAdresseNicht",
    "Too many requests": "vieleAnfragenBitteVersuche",
    "Internal server error": "internerFehlerAufgetreten",
    "Invalid origin": "anmeldungFehlgeschlagenBitteVersuche",
    INVALID_ORIGIN: "anmeldungFehlgeschlagenBitteVersuche",
    "Invalid callbackURL": "anmeldungFehlgeschlagenBitteVersuche",
    INVALID_CALLBACK_URL: "anmeldungFehlgeschlagenBitteVersuche",
    INVALID_EMAIL_OR_PASSWORD: "ungueltigeEMailPasswort",
  };
}

/** Message key of a Better-Auth code/message; for decisions, never shown. */
export function authErrorKey(message: string): string | undefined {
  return authErrorKeyMap()[message];
}

/**
 * Translate a Better-Auth error code/message to a user-facing string.
 *
 * `useTranslations("public.loginShared")`.
 */
export function translateAuthError(t: Translate, message: string): string {
  const key: string | undefined = authErrorKeyMap()[message];
  return t(key ?? "anmeldungFehlgeschlagen");
}
